/**
 * ExchangeContextBanner — slim, one-line ball-context banner (V-09 promotion).
 *
 * Replaces the former CatchballLoopShell wrapper (removed under DEP-009).
 * Canonical Track and Session routes render inside <ExchangeContextFrame>;
 * when the URL carries `?loopId=`, a single-line banner names the ball
 * ("the ball is always named", Doc 26 rule 4) and links back to the exchange
 * loop. Without the param the frame renders children bare — zero cost on
 * normal navigation.
 *
 * Also hosts CatchballLoopContext (moved from that removed shell) so pages like
 * TrackDetail keep detecting exchange context the same way.
 */

import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { RefreshCcw } from 'lucide-react';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import {
  normalizeLoopState,
  type CanonicalLoopState,
} from '../../../types/prioritization/catchballLoop';
import { buildCatchballInboxUrl } from '../../../lib/catchballInboxUrls';

// ─── Loop context (lets child pages detect catchball context) ────────────────

export interface CatchballLoopContextValue {
  loopId: string;
  cycleId: string | undefined;
}

// eslint-disable-next-line react-refresh/only-export-components
export const CatchballLoopContext = createContext<CatchballLoopContextValue | null>(null);

/** Returns the catchball loop context when the current URL carries ?loopId=, else null. */
// eslint-disable-next-line react-refresh/only-export-components
export function useCatchballLoopContext(): CatchballLoopContextValue | null {
  return useContext(CatchballLoopContext);
}

// ─── Ball naming ─────────────────────────────────────────────────────────────

const STATE_LABEL: Partial<Record<CanonicalLoopState, string>> = {
  draft: 'Ball: being prepared',
  requested: 'Ball: with the working tier',
  middle_working: 'Ball: with the working tier',
  proposal_submitted: 'Ball: returning up — proposal submitted',
  under_review: 'Ball: with the evaluator',
  approved: 'Ball: resolved — approved',
  returned: 'Ball: back down — returned with guidance',
  closed: 'Ball: resolved',
};

function ballLabel(state: string | undefined): string {
  if (!state) return 'Exchange context';
  try {
    const canonical = normalizeLoopState(state as never);
    return STATE_LABEL[canonical] ?? `Ball: ${String(canonical).replace(/_/g, ' ')}`;
  } catch {
    return `Ball: ${state.replace(/_/g, ' ')}`;
  }
}

// ─── Frame ───────────────────────────────────────────────────────────────────

export function ExchangeContextFrame({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const loopId = searchParams.get('loopId');

  if (!loopId) return <>{children}</>;
  return <ExchangeContextInner loopId={loopId}>{children}</ExchangeContextInner>;
}

function ExchangeContextInner({ loopId, children }: { loopId: string; children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const ballId = searchParams.get('ballId');
  const getLoop = useCatchballLoopStore((s) => s.getLoop);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const loop = getLoop(loopId);
  const cycle = useDegapProcessStore((s) =>
    loop?.cycleId ? s.cycles.find((c) => c.id === loop.cycleId) : undefined,
  );

  useEffect(() => {
    if (!loop) void fetchLoops();
  }, [loop, fetchLoops]);

  const inboxHref = loop?.cycleId
    ? buildCatchballInboxUrl(loop.cycleId, loopId, { ballId })
    : '/prioritization/cascade';

  return (
    <CatchballLoopContext.Provider value={{ loopId, cycleId: loop?.cycleId }}>
      <div className="flex items-center gap-2 px-4 py-1.5 text-xs bg-[var(--slate-dim)] border-b border-[var(--slate)] dark:bg-[rgba(99,102,241,0.08)] dark:border-[rgba(99,102,241,0.25)]">
        <RefreshCcw className="h-3 w-3 flex-shrink-0 text-[var(--text-ter)]" />
        <span className="font-medium text-[var(--text-sec)] whitespace-nowrap">
          {ballLabel(loop?.state as string | undefined)}
        </span>
        {cycle?.name && (
          <span className="text-[var(--text-ter)]/70 dark:text-[var(--text-sec)] truncate">
            · Exchange — {cycle.name}
          </span>
        )}
        <Link
          to={inboxHref}
          className="ml-auto font-semibold text-[var(--text-sec)] hover:text-[var(--text-pri)] dark:hover:text-white whitespace-nowrap underline-offset-2 hover:underline"
        >
          ← Back to Catchball
        </Link>
      </div>
      {children}
    </CatchballLoopContext.Provider>
  );
}
